<script setup lang="ts">
type LikeButtonVariant = 'simple' | 'normal' | 'count'

const props = withDefaults(
  defineProps<{
    liked?: boolean
    pending?: boolean
    count?: number
    variant?: LikeButtonVariant
    effectToken?: number
  }>(),
  {
    liked: false,
    pending: false,
    count: 0,
    variant: 'normal',
    effectToken: 0
  }
)

const emit = defineEmits<{ (e: 'click', event: MouseEvent): void }>()

const celebrating = ref(false)
let celebrateTimer: ReturnType<typeof setTimeout> | null = null

const buttonText = computed(() => {
  if (props.variant !== 'normal') {
    return String(props.count || 0)
  }
  if (props.pending) {
    return '点赞中'
  }
  return props.liked ? '已点赞' : '点赞'
})

const onClick = (event: MouseEvent) => {
  if (props.pending || props.liked) {
    return
  }
  emit('click', event)
}

const clearCelebrateTimer = () => {
  if (!celebrateTimer) {
    return
  }
  clearTimeout(celebrateTimer)
  celebrateTimer = null
}

const triggerCelebrate = () => {
  clearCelebrateTimer()
  celebrating.value = false
  requestAnimationFrame(() => {
    celebrating.value = true
    celebrateTimer = setTimeout(() => {
      celebrating.value = false
      celebrateTimer = null
    }, 760)
  })
}

watch(
  () => props.effectToken,
  (next, prev) => {
    if (next > 0 && next !== prev) {
      triggerCelebrate()
    }
  }
)

onBeforeUnmount(() => clearCelebrateTimer())
</script>

<template>
  <button
    type="button"
    class="like-button"
    :class="[`variant-${variant}`, { liked, pending, celebrating }]"
    @click="onClick"
  >
    <i class="like-button-icon" :class="liked ? 'fa-solid fa-heart' : 'fa-regular fa-heart'" aria-hidden="true" />
    <span>{{ buttonText }}</span>
  </button>
</template>

<style scoped>
.like-button {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  overflow: visible;
  transition:
    color 0.16s ease,
    background-color 0.16s ease,
    border-color 0.16s ease;
}

.like-button.variant-simple {
  border: 0;
  border-radius: 6px;
  padding: 3px 5px;
  color: var(--text-soft);
  background: transparent;
  font-size: 13px;
  line-height: 1;
}

.like-button.variant-normal,
.like-button.variant-count {
  border: 1px solid var(--line);
  border-radius: 10px;
  padding: 8px 12px;
  color: var(--text);
  background: var(--bg-soft);
}

.like-button.liked {
  color: var(--brand);
}

.like-button.variant-normal.liked,
.like-button.variant-count.liked {
  border-color: color-mix(in srgb, var(--brand) 42%, var(--line) 58%);
  background: color-mix(in srgb, var(--brand) 11%, var(--bg-soft) 89%);
}

.like-button.variant-simple.pending {
  opacity: 0.45;
}

.like-button-icon {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  font-size: 14px;
  line-height: 1;
}

.like-button-icon::before {
  display: block;
  line-height: 1;
}

.like-button.celebrating .like-button-icon::before {
  animation: like-button-icon-pop 0.68s ease-out;
  transform-origin: center;
}

.like-button.celebrating .like-button-icon::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  width: 18px;
  height: 18px;
  border: 1.6px solid color-mix(in srgb, var(--brand) 82%, transparent);
  border-radius: 999px;
  opacity: 0;
  transform: translate(-50%, -50%);
  pointer-events: none;
  animation: like-button-wave 0.62s ease-out forwards;
}

@keyframes like-button-icon-pop {
  0% {
    transform: scale(0.84);
  }
  22% {
    transform: scale(1.26);
  }
  40% {
    transform: scale(0.94);
  }
  58% {
    transform: scale(1.13);
  }
  74% {
    transform: scale(0.98);
  }
  88% {
    transform: scale(1.04);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes like-button-wave {
  0% {
    opacity: 0.38;
    transform: translate(-50%, -50%) scale(0.4);
  }
  45% {
    opacity: 0.18;
  }
  100% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(1.45);
  }
}

@media (prefers-reduced-motion: reduce) {
  .like-button.celebrating .like-button-icon::before,
  .like-button.celebrating .like-button-icon::after {
    animation: none;
  }
}
</style>
